import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageContainerService } from '../../services';
import {
  AskDataQuestionCommandRequest,
  AskDataQuestionCommandResponse,
  AskDataRowDto,
  GetProjectsByUserCommandRequest,
  GetProjectsByUserCommandResponse,
  ProjectDto
} from '../../data/CommandServiceDtos.generated';

/**
 * One exchange in the conversation. The question is echoed back so the thread reads as a transcript
 * rather than a form that keeps replacing its own answer.
 */
export interface AskDataTurn {
  question: string;
  answer: string;
  sql: string;
  columns: string[];
  rows: AskDataRowDto[];
  rowCount: number;
  truncated: boolean;
  /** True when the command came back with nothing — the server already raised a notification saying why. */
  failed: boolean;
  /** Per-turn toggle for the generated SQL, collapsed by default. */
  showSql: boolean;
}

/**
 * "Ask the data" — a chat panel that answers plain-language questions about the current project by
 * having the server generate a read-only SQL query, screen it, run it, and describe the rows.
 *
 * The SQL is deliberately shown (collapsed) next to every answer. A model-written query is only
 * trustworthy if the reader can check it, and hiding it would make a wrong answer indistinguishable
 * from a right one.
 *
 * All validation — unanswerable question, refused query, no project access — is reported by the command
 * as validation results, which the shared command pipeline has already surfaced as a notification by the
 * time the promise resolves. The turn is marked failed so the transcript shows the gap, without this
 * component trying to duplicate the message.
 */
@Component({
  standalone: false,
  selector: 'project-ask-data',
  templateUrl: './project-ask-data.component.html',
  styleUrls: ['./project-ask-data.component.scss'],
})
export class ProjectAskDataComponent implements OnInit, AfterViewInit {
  @ViewChild('transcript') transcript: ElementRef<HTMLDivElement>;
  @ViewChild('questionInput') questionInput: ElementRef<HTMLTextAreaElement>;

  projectId: number;
  projectName: string;
  question = '';
  asking = false;
  turns: AskDataTurn[] = [];

  /** Picker state, used only when the dialog opens without a project (the app-wide floating button). */
  projects: ProjectDto[] = [];
  loadingProjects = false;
  projectSearch = '';

  /** Starter prompts, so an empty panel suggests what it can actually answer. */
  readonly examples: string[] = [
    'How many active readiness tasks are there?',
    'Which disciplines have the most tasks?',
    'List the highest priority tasks and who owns them',
    'When is the project scheduled to finish?'
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private pcs: PageContainerService,
    private dialog: MatDialogRef<ProjectAskDataComponent>) {
    this.projectId = this.data ? this.data.projectId : 0;
    this.projectName = this.data ? this.data.projectName : '';
  }

  ngOnInit(): void {
    // Opened from the floating button on a page with no project in the URL: ask which one first.
    if (this.needsProject) {
      this.loadProjects();
    }
  }

  ngAfterViewInit(): void {
    this.focusInput();
  }

  /** True while there is no project to scope questions to, so the picker shows instead of the composer. */
  get needsProject(): boolean {
    return !this.projectId || this.projectId <= 0;
  }

  get filteredProjects(): ProjectDto[] {
    const term = (this.projectSearch || '').trim().toLowerCase();
    if (!term) {
      return this.projects;
    }
    return this.projects.filter(p => (p.Name || '').toLowerCase().indexOf(term) >= 0);
  }

  async loadProjects() {
    this.loadingProjects = true;
    try {
      const response: GetProjectsByUserCommandResponse = await this.pcs.apiService.GetProjectsByUser(
        {
          Page: 1,
          PageSize: 500,
          Skip: 0,
          ClientId: 0,
          AssetId: 0,
          ActiveFilter: 'Active',
          Search: '',
          Sort: '',
          FavoriteProjectIds: []
        } as GetProjectsByUserCommandRequest, true, false);

      // Sorted here rather than by passing a Sort key the repository would have to recognise.
      const projects = response && response.Projects ? response.Projects.slice() : [];
      projects.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
      this.projects = projects;
    } catch (error) {
      console.log('In ProjectAskDataComponent - loadProjects(), request failed.', error);
      this.projects = [];
    } finally {
      this.loadingProjects = false;
    }
  }

  chooseProject(project: ProjectDto) {
    this.projectId = project.ProjectId;
    this.projectName = project.Name;
    // The composer only exists once a project is chosen, so focus it on the next tick.
    this.focusInput();
  }

  /** Put the cursor in the question box so the panel is type-ready the moment it opens. */
  private focusInput() {
    setTimeout(() => {
      if (this.questionInput && this.questionInput.nativeElement) {
        this.questionInput.nativeElement.focus();
      }
    }, 0);
  }

  useExample(example: string) {
    this.question = example;
    this.ask();
  }

  async ask() {
    const question = (this.question || '').trim();
    if (!question || this.asking) {
      return;
    }

    this.asking = true;
    const turn: AskDataTurn = {
      question: question,
      answer: '',
      sql: '',
      columns: [],
      rows: [],
      rowCount: 0,
      truncated: false,
      failed: false,
      showSql: false,
    };
    this.turns.push(turn);
    this.question = '';
    this.scrollToLatest();

    try {
      const response: AskDataQuestionCommandResponse = await this.pcs.apiService.AskDataQuestion(
        { ProjectId: this.projectId, Question: question } as AskDataQuestionCommandRequest, true, false);

      if (!response) {
        turn.failed = true;
        return;
      }

      turn.answer = response.Answer || '';
      turn.sql = response.Sql || '';
      turn.columns = response.Columns || [];
      turn.rows = response.Rows || [];
      turn.rowCount = response.RowCount || 0;
      turn.truncated = !!response.Truncated;

      // No prose and no rows means the question was refused upstream; the notification explains it.
      turn.failed = !turn.answer && turn.rows.length === 0;
    } catch (error) {
      console.log('In ProjectAskDataComponent - ask(), request failed.', error);
      turn.failed = true;
    } finally {
      this.asking = false;
      this.scrollToLatest();
    }
  }

  /** Enter sends, Shift+Enter is a newline — what people expect of a chat box. */
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.ask();
    }
  }

  toggleSql(turn: AskDataTurn) {
    turn.showSql = !turn.showSql;
  }

  close() {
    this.dialog.close();
  }

  /** Values arrive pre-rendered as strings; nulls print as an em dash rather than the word "null". */
  display(value: string): string {
    return value === null || value === undefined || value === '' ? '—' : value;
  }

  private scrollToLatest() {
    // Defer past the render that adds the turn, otherwise we scroll before it has height.
    setTimeout(() => {
      if (this.transcript && this.transcript.nativeElement) {
        this.transcript.nativeElement.scrollTop = this.transcript.nativeElement.scrollHeight;
      }
    }, 0);
  }
}
